import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Tooltip from "@material-ui/core/Tooltip";
import Zoom from "@material-ui/core/Zoom";
import { FiInfo, FiPlus, FiTrash2 } from "react-icons/fi";
import { MdClose } from "react-icons/md";

const AdditionalRevenueModel = ({
  open = false,
  onClose = () => { },
  isReadOnly = false,
  titleSuffix = "",
  items = [],
  onAdd = () => { },
  onRemove = () => { },
  onRename = () => { },
  onUpdateAmount = () => { },
}) => {
  const totalMiscRevenue = items.reduce(
    (sum, item) => sum + Number(item?.amount || 0),
    0
  );

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Zoom}
      onClose={onClose}
      aria-labelledby="additional-revenue-modal-title"
      classes={{
        paper: "rounded-none overflow-hidden shadow-2xl border border-[#cbd5e1]",
      }}
    >
      {/* Header Bar */}
      <div className="flex items-center justify-between w-full px-3 py-2 bg-[#f8fafc] border-b border-[#cbd5e1] text-[#313C4C]">
        <div id="additional-revenue-modal-title" className="flex items-center gap-2">
          <Tooltip title="Additional revenue items not included in main calculations" placement="top" arrow>
            <div className="flex items-center gap-1.5 cursor-pointer">
              <h2 className="text-[11px] font-medium capitalize tracking-wide text-slate-800 m-0">
                Additional Revenue {titleSuffix ? `(${titleSuffix})` : ""}
              </h2>
              <FiInfo className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 transition-colors" />
            </div>
          </Tooltip>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1 text-slate-600 hover:text-slate-900 px-2 py-1 rounded-none border border-[#cbd5e1] hover:bg-slate-100 transition-all cursor-pointer text-[11px] font-medium capitalize"
        >
          <MdClose size={14} />
          <span className="hidden md:block text-[11px] font-medium capitalize">
            Close
          </span>
        </button>
      </div>

      {/* Dialog Body */}
      <DialogContent className="p-2 sm:p-3 bg-white">
        <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
          {/* Items Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-[11px] font-medium capitalize">
              <thead className="bg-[#e2e8f0] text-[#1e293b]">
                <tr className="h-[19px]">
                  <th scope="col" className="px-2 py-0.5 h-[19px] align-middle text-left font-medium text-[11px] text-[#1e293b] capitalize border-r border-b border-[#cbd5e1] w-[50%]">
                    Revenue Item Description
                  </th>
                  <th scope="col" className="px-2 py-0.5 h-[19px] align-middle text-left font-medium text-[11px] text-[#1e293b] capitalize border-r border-b border-[#cbd5e1] w-[30%]">
                    Amount
                  </th>
                  <th scope="col" className="px-2 py-0.5 h-[19px] align-middle text-right font-medium text-[11px] text-[#1e293b] capitalize border-r border-b border-[#cbd5e1] w-[20%] whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {items.length > 0 ? (
                  items.map((item, index) => {
                    const rowId = item.id ?? index;
                    return (
                      <tr key={rowId} className="h-[19px] hover:bg-slate-50/50 transition-colors">
                        {/* Description Cell */}
                        <td className="p-0 h-[19px] align-middle border-r border-b border-[#cbd5e1] bg-[#ffffd6] focus-within:bg-white focus-within:ring-1 focus-within:ring-amber-500 focus-within:ring-inset focus-within:z-10 relative">
                          <input
                            type="text"
                            disabled={isReadOnly}
                            value={item.label ?? ""}
                            onChange={(e) => onRename(item, index, e.target.value)}
                            placeholder="Item name (e.g. Demurrage, Misc Revenue...)"
                            className="w-full h-[19px] block px-2 py-0 rounded-none border-none outline-none focus:outline-none focus:ring-0 bg-transparent text-slate-800 placeholder:text-slate-400 disabled:bg-[#f0f4f9] disabled:text-slate-400 disabled:cursor-not-allowed font-medium text-[11px] capitalize cargo-table-input"
                          />
                        </td>

                        {/* Amount Cell */}
                        <td className="p-0 h-[19px] align-middle border-r border-b border-[#cbd5e1] bg-[#ffffd6] focus-within:bg-white focus-within:ring-1 focus-within:ring-amber-500 focus-within:ring-inset focus-within:z-10 relative">
                          <input
                            type="number"
                            disabled={isReadOnly}
                            value={item.amount === 0 ? "" : item.amount ?? ""}
                            onChange={(e) => onUpdateAmount(item, index, e.target.value)}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="w-full h-[19px] block px-2 py-0 rounded-none border-none outline-none focus:outline-none focus:ring-0 bg-transparent text-slate-800 font-medium text-[11px] capitalize placeholder:text-slate-400 disabled:bg-[#f0f4f9] disabled:text-slate-400 disabled:cursor-not-allowed cargo-table-input"
                          />
                        </td>

                        {/* Actions Cell */}
                        <td className="px-1 py-0 h-[19px] align-middle text-right whitespace-nowrap border-r border-b border-[#cbd5e1] bg-white">
                          {!isReadOnly && (
                            <button
                              type="button"
                              onClick={() => onRemove(item, index)}
                              className="p-0.5 rounded-none transition-colors text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer inline-flex items-center justify-center"
                              title="Delete revenue item"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={3} className="px-2 py-2 text-center text-slate-400 italic text-[11px] font-medium capitalize border-r border-b border-[#cbd5e1]">
                      No revenue items added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Subtable Footer / Actions & Summary */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5 bg-[#f0f4f9] border-t border-[#cbd5e1]">
            <div>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={onAdd}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium capitalize rounded-none bg-[#313C4C] text-white hover:bg-[#1e2632] transition cursor-pointer"
                >
                  <FiPlus className="w-3 h-3" />
                  <span className="text-[11px] font-medium capitalize">Add Revenue Item</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 text-[11px] font-medium capitalize">
              <span className="text-slate-600 font-medium capitalize">
                Total Revenue:
              </span>
              <div className="px-2 py-0.5 rounded-none bg-[#f0f4f9] border border-[#cbd5e1] text-[#1e293b] font-medium text-[11px]">
                {formatCurrency(totalMiscRevenue)}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* Modal Actions Footer */}
      <div className="flex items-center justify-end px-3 py-2 border-t border-[#cbd5e1] bg-white">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-1 text-[11px] font-medium capitalize text-slate-700 hover:bg-slate-100 border border-[#cbd5e1] rounded-none transition cursor-pointer"
        >
          Save
        </button>
      </div>
    </Dialog>
  );
};

export default AdditionalRevenueModel;
